import { useCallback } from 'react'
import {
    FIELD_LIMITS,
    truncateField,
    normalizeToArray,
    validateAuthors,
    validateCategories,
} from '../utils/validation'

/**
 * Parsed book data from file import
 */
export interface ParsedBook {
    title?: string
    author?: string[]
    category?: string[]
    synopsis?: string
    description?: string
    page_count?: number | string
    publisher?: string
    series?: string
    series_number?: number | string
    location?: string
    status?: string
    progress?: string
    format?: string
    notes?: string
    language?: string
}

/**
 * Return type for useFileImport hook
 */
interface UseFileImportReturn {
    parseCSVLine: (text: string) => string[]
    parseFile: (content: string, type: 'csv' | 'json') => ParsedBook[]
    readAndParseFile: (file: File) => Promise<ParsedBook[]>
}

/**
 * Shared hook for file import operations (CSV, JSON)
 * 
 * Used by both BookScanner and BookList to eliminate code duplication
 */
export function useFileImport(): UseFileImportReturn {
    /**
     * Parse a CSV line handling quoted values correctly
     */
    const parseCSVLine = useCallback((text: string): string[] => {
        const results: string[] = []
        let current = ''
        let inQuote = false
        let i = 0

        while (i < text.length) {
            const char = text[i]
            if (char === '"') {
                if (inQuote && i + 1 < text.length && text[i + 1] === '"') {
                    current += '"'
                    i += 2 // Skip both double quotes
                    continue
                } else {
                    inQuote = !inQuote
                }
            } else if (char === ',' && !inQuote) {
                results.push(current.trim())
                current = ''
            } else if (char) {
                current += char
            }
            i++
        }
        results.push(current.trim())
        return results
    }, [])

    /**
     * Parse import file content (CSV or JSON)
     */
    const parseFile = useCallback(
        (content: string, type: 'csv' | 'json'): ParsedBook[] => {
            try {
                let parsed: Record<string, unknown>[] = []

                if (type === 'json') {
                    parsed = JSON.parse(content)
                } else if (type === 'csv') {
                    const lines = content.split('\n').filter((l) => l.trim())
                    const headers = parseCSVLine(lines[0]).map((h) => h.replace(/(^")|("$)/g, '').trim())

                    parsed = lines.slice(1).map((line) => {
                        const values = parseCSVLine(line).map((v) => v.replace(/(^")|("$)/g, '').trim())
                        const obj: Record<string, string> = {}

                        headers.forEach((h, i) => {
                            let key = h.toLowerCase()

                            // Map Spanish column names to English keys
                            if (key === 'título' || key === 'titulo') key = 'title'
                            else if (key === 'autor') key = 'author'
                            else if (key === 'categoría' || key === 'category' || key === 'category') key = 'category'
                            else if (key === 'language') key = 'language'
                            else if (key === 'editorial') key = 'publisher'
                            else if (key === 'series') key = 'series'
                            else if (key === 'formato') key = 'format'
                            else if (key === 'estado') key = 'status'
                            else if (key === 'progreso') key = 'progress'
                            else if (key === 'ubicación' || key === 'ubicacion') key = 'location'
                            else if (key === 'notas') key = 'notes'
                            else if (key === 'número de series' || key === 'numero series' || key === 'series_number' || key === '#') key = 'series_number'

                            const val = values[i]
                            if (val !== undefined && val !== null) {
                                obj[key] = val
                            }
                        })
                        return obj
                    })
                }

                // Normalize and validate data
                const b = FIELD_LIMITS.books

                return parsed.map((p: Record<string, unknown>): ParsedBook => {
                    const normalizedAuthor = normalizeToArray(p.author as string | string[] | undefined)
                    const normalizedCategory = normalizeToArray(p.category as string | string[] | undefined)

                    // Filter and build only valid fields
                    const validatedBook: ParsedBook = {
                        author: validateAuthors(normalizedAuthor),
                        category: validateCategories(normalizedCategory),
                    }

                    // Only add fields that exist in schema and have values
                    if (typeof p.title === 'string') validatedBook.title = p.title
                    if (typeof p.synopsis === 'string') validatedBook.synopsis = truncateField(p.synopsis, b.synopsis) || undefined
                    if (typeof p.description === 'string') validatedBook.description = truncateField(p.description, b.description) || undefined
                    if (typeof p.page_count === 'number' || typeof p.page_count === 'string') validatedBook.page_count = p.page_count
                    if (typeof p.publisher === 'string') validatedBook.publisher = truncateField(p.publisher, b.publisher) || undefined
                    if (typeof p.series === 'string') validatedBook.series = truncateField(p.series, b.series) || undefined
                    if (typeof p.series_number === 'number' || typeof p.series_number === 'string') validatedBook.series_number = p.series_number
                    if (typeof p.location === 'string') validatedBook.location = truncateField(p.location, b.location) || undefined
                    if (typeof p.status === 'string') validatedBook.status = p.status
                    if (typeof p.progress === 'string') validatedBook.progress = p.progress
                    if (typeof p.format === 'string') validatedBook.format = p.format
                    if (typeof p.notes === 'string') validatedBook.notes = truncateField(p.notes, b.notes) || undefined
                    if (typeof p.language === 'string') validatedBook.language = truncateField(p.language, b.language) || undefined

                    return validatedBook
                })
            } catch (e) {
                console.error('Error parsing file:', e)
                throw new Error('Error al leer el archivo. Asegúrate de que el formato sea correcto.', { cause: e })
            }
        },
        [parseCSVLine]
    )

    /**
     * Read file and parse it
     */
    const readAndParseFile = useCallback(
        (file: File): Promise<ParsedBook[]> => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()

                reader.onload = (evt) => {
                    try {
                        const content = evt.target?.result as string
                        const fileName = file.name.toLowerCase()
                        let type: 'csv' | 'json'

                        if (fileName.endsWith('.json')) {
                            type = 'json'
                        } else if (fileName.endsWith('.csv')) {
                            type = 'csv'
                        } else {
                            throw new Error('Formato no soportado. Usa CSV o JSON.')
                        }

                        const data = parseFile(content, type)
                        resolve(data)
                    } catch (error) {
                        reject(error)
                    }
                }

                reader.onerror = () => reject(new Error('Error al leer el archivo'))
                reader.readAsText(file)
            })
        },
        [parseFile]
    )

    return {
        parseCSVLine,
        parseFile,
        readAndParseFile,
    }
}
